import React, { useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import { ModalOverlay } from "../../../components/ModalOverlay";
import FullregisterPatient from "../FullregisterPatient";
import type { SummaryTheme } from "./patientSummaryUi";

type FullPatientEditModalProps = {
  open: boolean;
  onClose: () => void;
  patientId?: string;
  theme?: SummaryTheme;
  onSaved: () => void;
};

export default function FullPatientEditModal({
  open,
  onClose,
  patientId,
  theme = "light",
  onSaved,
}: FullPatientEditModalProps) {
  const isDark = theme === "dark";

  useEffect(() => {
    if (!open) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.body.style.overflow = "hidden";
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = "";
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, onClose]);

  if (!open || !patientId) return null;

  return (
    <ModalOverlay
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="full-patient-edit-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-label="Close dialog"
      />
      <div
        className={`relative z-10 flex max-h-[94dvh] w-full max-w-5xl flex-col overflow-hidden rounded-t-2xl sm:rounded-2xl shadow-xl ${
          isDark ? "bg-slate-900" : "bg-slate-50"
        }`}
      >
        <div
          className={`flex items-center justify-between gap-3 border-b px-4 py-3 sm:px-5 ${
            isDark ? "border-slate-700 bg-slate-900" : "border-slate-200 bg-white"
          }`}
        >
          <div>
            <h2
              id="full-patient-edit-title"
              className={`text-base font-semibold ${
                isDark ? "text-slate-100" : "text-slate-900"
              }`}
            >
              Edit patient
            </h2>
            <p
              className={`text-xs ${
                isDark ? "text-slate-400" : "text-slate-500"
              }`}
            >
              Full patient details and medical history
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={`rounded-lg p-2 transition-colors ${
              isDark
                ? "text-slate-300 hover:bg-slate-700"
                : "text-slate-500 hover:bg-slate-100"
            }`}
            aria-label="Close"
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3 sm:px-4 sm:py-4">
          <FullregisterPatient
            key={patientId}
            patientId={patientId}
            embedded
            startEditable
            onSuccess={() => {
              onSaved();
              onClose();
            }}
            onCancel={onClose}
          />
        </div>
      </div>
    </ModalOverlay>
  );
}
